import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Navigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { acceptTerms } from "@/api/user";
import { ROUTES } from "@/constants/routeConstants";
import {
  BrandingSidebar,
  OnboardingLayout,
} from "@/features/onboarding/components/onboarding-layout";
import { OrganizationLoadError } from "@/features/onboarding/organization-load-error";
import { queryKeys } from "@/lib/query-keys";
import { useAuthStore } from "@/store/auth";
import { useOrganizationStore } from "@/store/organization";
import {
  ACCOUNT_NUX_PROGRESS_STEPS,
  ACCOUNT_NUX_STEPS,
  type AccountNuxStep,
} from "./constants";
import { useSignupConfig } from "./hooks/useSignupConfig";
import { useWalletStatus } from "./hooks/useWalletStatus";
import { resetSetupIntentFormKey } from "./lib/setup-intent-form-key";
import { setupIntentQueryOptions } from "./lib/setup-intent-query";
import { ActivatingStep } from "./steps/activating-step";
import { OrganizationSignupRecoveryStep } from "./steps/organization-signup-recovery-step";
import { OrganizationStep } from "./steps/organization-step";
import { PaymentStep } from "./steps/payment-step";
import { PricingStep } from "./steps/pricing-step";
import { WelcomeStep } from "./steps/welcome-step";

/**
 * Renders the ordered self-serve signup flow for flag-enabled new users and
 * admins of pending self-serve organizations. Persisted enrollment remains
 * authoritative after organization creation: only an explicit enrollment marker
 * on a pending admin organization resumes NUX, so a flag outage cannot strand
 * the signup. New eligible accounts render Welcome, then leave NUX as soon as
 * organization creation publishes an active organization. Enrolled pending
 * admins resume the legacy billing flow from wallet state, and activation
 * publishes the active organization before the status gate completes dashboard
 * navigation.
 * Organization-scoped progress is ignored synchronously after a selection
 * change, while organization-creation state survives an optimistic switch
 * rollback so the existing organization can be retried instead of recreated.
 * Organization-load failures render a recoverable error; loading renders
 * nothing; other users redirect to onboarding or home with browser-history
 * replacement.
 *
 * @returns The current NUX step, fallback error, redirect, or `null` while loading.
 */
export const AccountNux = () => {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const setAuthState = useAuthStore((s) => s.setAuthState);
  const organization = useOrganizationStore((s) => s.organization);
  const isOrgLoading = useOrganizationStore((s) => s.isLoading);
  const isOrganizationSwitching = useOrganizationStore((s) => s.isSwitching);
  const organizationError = useOrganizationStore((s) => s.error);
  const {
    data: signupConfig,
    isError: isConfigError,
    isFetching: isConfigFetching,
  } = useSignupConfig();
  const organizationId = organization?.id ?? null;
  const isSelfServeProvisioning =
    organization?.account_status === "provisioning" &&
    organization.self_serve_billing_enabled === true;
  // Only users explicitly marked by first-login provisioning enter consent;
  // legacy profiles remain grandfathered during a rolling deploy.
  const requiresTermsAcceptance = user?.requires_terms_acceptance === true;
  const walletStatus = useWalletStatus(
    requiresTermsAcceptance || isSelfServeProvisioning
      ? undefined
      : organization?.id,
  );
  const [flowState, setFlowState] = useState<{
    organizationId: string | null;
    step: AccountNuxStep | null;
    paymentRecoveryMessage: string | null;
  }>(() => ({
    organizationId,
    step: null,
    paymentRecoveryMessage: null,
  }));
  const selfServeEnabled =
    !isConfigError && signupConfig?.self_serve_signup_enabled === true;
  const ownsSelectedOrganization = flowState.organizationId === organizationId;
  // Creation must survive setOrganization's optimistic selection and rollback.
  const latchedStep =
    ownsSelectedOrganization ||
    flowState.step === ACCOUNT_NUX_STEPS.ORGANIZATION
      ? flowState.step
      : null;
  const paymentRecoveryMessage = ownsSelectedOrganization
    ? flowState.paymentRecoveryMessage
    : null;

  // Resume from saved billing state. An unexpired Stripe invoice-default card
  // advances to activation; it does not assert authorization or fraud clearance.
  // Wallet-status failures fall back to pricing so the step can be re-derived.
  const resumeStep: AccountNuxStep = !organization
    ? ACCOUNT_NUX_STEPS.WELCOME
    : !walletStatus.isError && walletStatus.data?.has_unexpired_default_card
      ? ACCOUNT_NUX_STEPS.ACTIVATING
      : !walletStatus.isError && walletStatus.data?.has_wallet
        ? ACCOUNT_NUX_STEPS.PAYMENT
        : ACCOUNT_NUX_STEPS.PRICING;
  const canLatchResumeStep = organization
    ? !walletStatus.isFetching && walletStatus.isFetched
    : !isOrgLoading && !isConfigFetching && selfServeEnabled;

  useEffect(() => {
    if (latchedStep === null && canLatchResumeStep) {
      setFlowState({
        organizationId,
        step: resumeStep,
        paymentRecoveryMessage: null,
      });
    }
  }, [canLatchResumeStep, latchedStep, organizationId, resumeStep]);

  const currentStep = requiresTermsAcceptance
    ? ACCOUNT_NUX_STEPS.WELCOME
    : isSelfServeProvisioning
      ? ACCOUNT_NUX_STEPS.SIGNUP_RECOVERY
      : (latchedStep ?? resumeStep);

  const advanceTo = (step: AccountNuxStep) => {
    setFlowState({
      organizationId,
      step,
      paymentRecoveryMessage: null,
    });
  };

  const acceptTermsMutation = useMutation({
    mutationFn: acceptTerms,
    onSuccess: () => {
      const currentUser = useAuthStore.getState().user;
      if (currentUser && currentUser.id === user?.id) {
        const acceptedUser = {
          ...currentUser,
          requires_terms_acceptance: false,
        };
        setAuthState({ user: acceptedUser });
        queryClient.setQueryData(queryKeys.user.me(), acceptedUser);
      }
      // A user who already belongs to an organization never creates one.
      // Clearing the latch instead of advancing lets the resume effect derive
      // their step from wallet state — the query is only enabled once consent
      // stops gating it — or lets the status guards route them out of NUX.
      setFlowState({
        organizationId,
        step: organization ? null : ACCOUNT_NUX_STEPS.ORGANIZATION,
        paymentRecoveryMessage: null,
      });
    },
  });

  if (!organization && organizationError) {
    return <OrganizationLoadError />;
  }

  if (isOrgLoading || (!organization && isConfigFetching)) {
    return null;
  }
  if (!organization && !selfServeEnabled && !requiresTermsAcceptance) {
    return <Navigate to={ROUTES.ONBOARDING} replace={true} />;
  }

  if (
    organization &&
    organization.account_status !== "pending_approval" &&
    !isSelfServeProvisioning &&
    !isOrganizationSwitching &&
    !requiresTermsAcceptance
  ) {
    return <Navigate to={ROUTES.HOME} replace={true} />;
  }

  const isSelfServeAdmin =
    organization?.self_serve_billing_enabled === true &&
    organization?.role === "admin";

  if (
    (organization?.account_status === "pending_approval" ||
      isSelfServeProvisioning) &&
    !isSelfServeAdmin &&
    !requiresTermsAcceptance
  ) {
    return <Navigate to={ROUTES.ONBOARDING} replace={true} />;
  }

  if (
    organization &&
    walletStatus.isFetching &&
    !walletStatus.isFetchedAfterMount &&
    currentStep !== ACCOUNT_NUX_STEPS.ORGANIZATION
  ) {
    return null;
  }

  const indicatorStep =
    currentStep === ACCOUNT_NUX_STEPS.SIGNUP_RECOVERY
      ? ACCOUNT_NUX_STEPS.ORGANIZATION
      : currentStep === ACCOUNT_NUX_STEPS.ACTIVATING
        ? ACCOUNT_NUX_STEPS.PAYMENT
        : currentStep;
  const progressSteps =
    organization && requiresTermsAcceptance
      ? [ACCOUNT_NUX_STEPS.WELCOME]
      : organization?.account_status === "pending_approval"
        ? ACCOUNT_NUX_PROGRESS_STEPS
        : ACCOUNT_NUX_PROGRESS_STEPS.slice(0, 2);
  const stepNumber = progressSteps.indexOf(indicatorStep) + 1;
  const usesFullWidthLayout =
    currentStep === ACCOUNT_NUX_STEPS.WELCOME ||
    currentStep === ACCOUNT_NUX_STEPS.ORGANIZATION;
  const recoverPaymentSetup = (message: string) => {
    if (organization?.id) {
      resetSetupIntentFormKey(organization.id);
      queryClient.removeQueries({
        queryKey: queryKeys.billing.setupIntent(organization.id),
        exact: true,
      });
    }
    setFlowState({
      organizationId,
      step: ACCOUNT_NUX_STEPS.PAYMENT,
      paymentRecoveryMessage: message,
    });
  };
  const preparePayment = async () => {
    const expectedOrganizationId = organization?.id;
    if (!expectedOrganizationId) return;

    await queryClient.fetchQuery(
      setupIntentQueryOptions(expectedOrganizationId),
    );
    if (
      useOrganizationStore.getState().organization?.id !==
      expectedOrganizationId
    ) {
      return;
    }
    advanceTo(ACCOUNT_NUX_STEPS.PAYMENT);
  };

  return (
    <OnboardingLayout
      sidebar={
        usesFullWidthLayout ? undefined : (
          <BrandingSidebar
            description={
              organization && requiresTermsAcceptance
                ? "Review consent and access your organization's Sully.ai API workspace."
                : isSelfServeProvisioning
                  ? "Finish preparing your API credits and start building with the Sully.ai API."
                  : organization?.account_status === "pending_approval"
                    ? "Complete your saved billing setup and start building with the Sully.ai API."
                    : "Create your organization and start building with the Sully.ai API in minutes."
            }
            featuresHeading="Your console provides:"
          />
        )
      }
      footer={
        usesFullWidthLayout ? undefined : (
          <div className="mt-2 text-center text-sm text-muted-foreground">
            Step {stepNumber} of {progressSteps.length}
          </div>
        )
      }
    >
      {currentStep === ACCOUNT_NUX_STEPS.WELCOME && (
        <WelcomeStep
          requiresBillingSetup={
            organization?.account_status === "pending_approval"
          }
          hasOrganization={Boolean(organization && requiresTermsAcceptance)}
          requiresTermsAcceptance={requiresTermsAcceptance}
          isSubmitting={acceptTermsMutation.isPending}
          errorMessage={
            acceptTermsMutation.isError
              ? "We couldn't save your consent. Please try again."
              : null
          }
          onContinue={() => {
            if (!requiresTermsAcceptance) {
              advanceTo(ACCOUNT_NUX_STEPS.ORGANIZATION);
              return;
            }
            acceptTermsMutation.mutate();
          }}
        />
      )}
      {currentStep === ACCOUNT_NUX_STEPS.ORGANIZATION && (
        <OrganizationStep
          onContinue={() => advanceTo(ACCOUNT_NUX_STEPS.PRICING)}
          onBack={() => advanceTo(ACCOUNT_NUX_STEPS.WELCOME)}
        />
      )}
      {currentStep === ACCOUNT_NUX_STEPS.PRICING && (
        <PricingStep
          onContinue={preparePayment}
          onBack={() => advanceTo(ACCOUNT_NUX_STEPS.ORGANIZATION)}
        />
      )}
      {currentStep === ACCOUNT_NUX_STEPS.PAYMENT && (
        <PaymentStep
          key={organization?.id ?? "unavailable"}
          onBack={() => advanceTo(ACCOUNT_NUX_STEPS.PRICING)}
          initialErrorMessage={paymentRecoveryMessage}
        />
      )}
      {currentStep === ACCOUNT_NUX_STEPS.ACTIVATING && (
        <ActivatingStep
          key={organization?.id ?? "unavailable"}
          onPaymentRequired={recoverPaymentSetup}
        />
      )}
      {currentStep === ACCOUNT_NUX_STEPS.SIGNUP_RECOVERY && (
        <OrganizationSignupRecoveryStep
          key={organization?.id ?? "unavailable"}
        />
      )}
    </OnboardingLayout>
  );
};
